import React from 'react';
import './OrdersTable.css';
import Pagination from './Pagination';
import TraderDetailsPanel from './TraderDetailsPanel';
import useUrlDetails from '../hooks/useUrlDetails';

const TradersTable = ({
  data,
  activeRole = 'all',
  pagination,
  onPageChange,
  onAddClick,
  onEdit,
  onAddBalance,
  balanceRequestLoadingRef
}) => {

  const getTraderRole = (trader) => String(
    trader.role || trader.Role || trader.ROLE || 'trader'
  ).toLowerCase();

  const getTraderTraffic = (trader) => {
    const value = trader.traffic ?? trader.Traffic ?? trader.TRAFFIC;
    const normalizedValue = String(value ?? 'off').trim().toLowerCase();

    return ['on', 'true', '1', 'enabled'].includes(normalizedValue) ? 'on' : 'off';
  };

  const getAccountId = (trader) => {
    if (getTraderRole(trader) === 'gateway') {
      return trader.gatewayId || trader.GatewayId || trader.GATEWAY_ID ||
        trader.id || trader.Id || trader.ID || trader.ref || '-';
    }

    return trader.traderId || trader.TraderId || trader.TRADER_ID || trader.ref || '-';
  };

  const idColumnLabel = activeRole === 'trader'
    ? 'Trader ID'
    : activeRole === 'gateway'
      ? 'Gateway ID'
      : 'Account ID';

  // Extract data - support both the new paginated format and the old format
  let traders = [];
  
  if (data && data.data && Array.isArray(data.data)) {
    // New paginated format
    traders = data.data;
  } else if (Array.isArray(data)) {
    // Old format - just an array
    traders = data;
  } else if (data && data.ops && data.ops.length > 0) {
    // Backward compatibility with the old structure
    const firstOp = data.ops[0];
    if (firstOp.list && Array.isArray(firstOp.list)) {
      traders = firstOp.list;
    } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
      traders = firstOp.data.list;
    }
  }

  const [selectedTrader, setSelectedTrader] = useUrlDetails(traders, 'traderId');

  // Sort by creation date (create_time) - newest first
  traders.sort((a, b) => {
    const timeA = a.create_time || 0;
    const timeB = b.create_time || 0;
    // Sort in descending order (newest first)
    return timeB - timeA;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp * 1000); // If timestamp is in seconds
      if (isNaN(date.getTime())) {
        const date2 = new Date(timestamp); // Try as milliseconds
        if (isNaN(date2.getTime())) return String(timestamp);
        return formatCustomDate(date2);
      }
      return formatCustomDate(date);
    } catch {
      return String(timestamp);
    }
  };

  const formatCustomDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return (
      <div className="traders-date-cell">
        <span>{day}.{month}.{year}</span>
        <span>{hours}:{minutes}:{seconds}</span>
      </div>
    );
  };

  // Determine the columns to display
  const allColumns = [
    { 
      key: 'Created', 
      label: 'Created', 
      getValue: (trader) => {
        const created = trader.create_time || null;
        return formatDate(created);
      }
    },
    { 
      key: 'TraderId', 
      label: idColumnLabel,
      getValue: getAccountId
    },
    { 
      key: 'Login', 
      label: 'Login', 
      getValue: (trader) => {
        return trader.login || trader.Login || trader.LOGIN || '-';
      }
    },
    { 
      key: 'Name', 
      label: 'Name', 
      getValue: (trader) => {
        return trader.name || trader.Name || trader.NAME || '-';
      }
    },
    { 
      key: 'Role', 
      label: 'Role', 
      getValue: (trader) => {
        const role = trader.role || trader.Role || trader.ROLE || 'trader';
        return role;
      }
    },
    {
      key: 'Traffic',
      label: 'Traffic',
      roles: ['trader'],
      getValue: (trader) => {
        if (getTraderRole(trader) !== 'trader') return null;

        const traffic = getTraderTraffic(trader);
        return (
          <span className={`traffic-indicator traffic-${traffic}`}>
            {traffic === 'on' ? 'On' : 'Off'}
          </span>
        );
      }
    },
    {
      key: 'PayInCoefficient',
      label: 'Pay In Coefficient',
      roles: ['trader'],
      getValue: (trader) => {
        if (getTraderRole(trader) !== 'trader') return null;

        return trader.payInCoefficient || trader.PayInCoefficient || trader.PAYIN_COEFFICIENT || '-';
      }
    },
    {
      key: 'Less500PayInCoefficient',
      label: 'LESS500 Pay In Coefficient',
      roles: ['trader'],
      getValue: (trader) => {
        if (getTraderRole(trader) !== 'trader') return null;

        return trader.less500PayInCoefficient ??
          trader.Less500PayInCoefficient ??
          trader.LESS500PayInCoefficient ??
          trader.LESS500_PAYIN_COEFFICIENT ??
          trader.LESS500_PAY_IN_COEFFICIENT ??
          '-';
      }
    },
    {
      key: 'PayOutCoefficient',
      label: 'Pay Out Coefficient',
      roles: ['trader'],
      getValue: (trader) => {
        if (getTraderRole(trader) !== 'trader') return null;

        return trader.payOutCoefficient || trader.PayOutCoefficient || trader.PAYOUT_COEFFICIENT || '-';
      }
    },
    { 
      key: 'Balance', 
      label: 'Balance',
      roles: ['trader'],
      getValue: (trader) => {
        if (getTraderRole(trader) !== 'trader') return null;

        const balance = trader.balance || trader.Balance || trader.BALANCE;
        if (!balance) return '-';
        
        if (typeof balance === 'object' && balance !== null) {
          const current = balance.current || balance.Current || balance.CURRENT || '0';
          const pending = balance.pending || balance.Pending || balance.PENDING || '0';
          const total = balance.total || balance.Total || balance.TOTAL || '0';
          const traderRef = trader.ref || trader.traderId || trader.TraderId || trader.TRADER_ID || '';
          const isLoading = balanceRequestLoadingRef === traderRef;
          
          return (
            <div className="trader-balance-cell">
              <span className="trader-balance-value"><small>Current</small>{current} USDT</span>
              <span className="trader-balance-value"><small>Pending</small>{pending} USDT</span>
              <span className="trader-balance-value"><small>Total</small>{total} USDT</span>
              {onAddBalance && (
                <button
                  type="button"
                  className="inline-add-button"
                  disabled={isLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAddBalance(trader);
                  }}
                >
                  {isLoading ? 'Adding...' : 'Add'}
                </button>
              )}
            </div>
          );
        }
        
        return String(balance);
      }
    },
    {
      key: 'Status',
      label: 'Status',
      getValue: (trader) => {
        return trader.status || trader.Status || trader.STATUS || '-';
      }
    }
  ];

  const displayColumns = activeRole === 'all'
    ? allColumns
    : allColumns.filter(column =>
        column.key !== 'Role' && (!column.roles || column.roles.includes(activeRole))
      );

  const listLabel = activeRole === 'all'
    ? 'Accounts'
    : `${activeRole.charAt(0).toUpperCase()}${activeRole.slice(1)} accounts`;

  if (traders.length === 0) {
    return (
      <div className="orders-table-container">
        <div className="orders-header">
          <h3>{listLabel} (0)</h3>
          {onAddClick && (
            <button 
              className="add-button"
              onClick={onAddClick}
            >
              Add
            </button>
          )}
        </div>
        <div className="page-placeholder">
          <p>No accounts found</p>
        </div>
      </div>
    );
  }

  const totalItems = pagination?.total || traders.length;

  return (
    <>
      <div className="orders-table-container">
        <div className="orders-header">
          <h3>{listLabel} ({totalItems})</h3>
          {onAddClick && (
            <button 
              className="add-button"
              onClick={onAddClick}
            >
              Add
            </button>
          )}
        </div>
        <div className="table-wrapper">
          <table className={`orders-table traders-mobile-table traders-filter-${activeRole}`}>
            <thead>
              <tr>
                {displayColumns.map((column) => (
                  <th key={column.key} className={`header-${column.key.toLowerCase()}`}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {traders.map((trader, index) => {
                const status = (trader.status || trader.Status || trader.STATUS || '').toLowerCase();
                return (
                  <tr 
                    key={trader.ref || trader.traderId || trader.TraderId || index}
                    className={`order-row ${getTraderRole(trader) === 'trader' ? 'has-trader-balance' : 'without-trader-balance'}`}
                    onClick={() => setSelectedTrader(trader)}
                  >
                    {displayColumns.map((column) => {
                      const isStatusColumn = column.key === 'Status';
                      const isHiddenBalance = column.key === 'Balance' && getTraderRole(trader) !== 'trader';
                      const isHiddenTraffic = column.key === 'Traffic' && getTraderRole(trader) !== 'trader';
                      const isHiddenCoefficient = (
                        column.key === 'PayInCoefficient' ||
                        column.key === 'Less500PayInCoefficient' ||
                        column.key === 'PayOutCoefficient'
                      ) && getTraderRole(trader) !== 'trader';
                      const statusClass = isStatusColumn ? `status-${status}` : '';
                      return (
                        <td 
                          key={column.key} 
                          className={`column-${column.key.toLowerCase()} ${statusClass}${isHiddenBalance ? ' is-hidden-balance' : ''}${isHiddenTraffic ? ' is-hidden-traffic' : ''}${isHiddenCoefficient ? ' is-hidden-coefficient' : ''}`}
                          data-label={column.label}
                        >
                          {column.getValue(trader)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pagination && onPageChange && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            itemsPerPage={pagination.limit}
            totalItems={pagination.total}
          />
        )}
      </div>
      {selectedTrader && (
        <TraderDetailsPanel 
          trader={selectedTrader} 
          onClose={() => setSelectedTrader(null)}
          onEdit={(trader) => {
            if (onEdit) {
              onEdit(trader);
            }
            setSelectedTrader(null);
          }}
        />
      )}
    </>
  );
};

export default TradersTable;
